import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const LOCAL_LIST_ID = 'local_list_default';
const LOCAL_TASKS_KEY = 'lovable_local_tasks';

export interface List {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  position: number;
}

interface ListContextType {
  lists: List[];
  activeListId: string | null;
  setActiveListId: (id: string | null) => void;
  addList: (name: string) => Promise<string | null>;
  updateList: (id: string, name: string) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  reorderLists: (sourceIndex: number, destinationIndex: number) => Promise<void>;
  loading: boolean;
  showCreateListModal: boolean;
  setShowCreateListModal: (show: boolean) => void;
}

const ListContext = createContext<ListContextType | undefined>(undefined);

export const ListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lists, setLists] = useState<List[]>([]);
  const [activeListIdState, setActiveListIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const { session, user } = useAuth();

  // For non-authenticated users, provide a default local list
  const defaultLocalList = {
    id: LOCAL_LIST_ID,
    name: 'My First List',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    user_id: 'local',
    position: 0
  };

  const setActiveListId = (listId: string | null) => {
    setActiveListIdState(listId);
    if (listId) {
      if (session) {
        localStorage.setItem('activeListId', listId);
      } else {
        // For local users, always use the local list
        localStorage.setItem('activeListId', LOCAL_LIST_ID);
      }
    }
  };

  const fetchLists = async () => {
    if (!session) {
      // For non-authenticated users, provide the default local list
      setLists([defaultLocalList]);
      setActiveListId(LOCAL_LIST_ID);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('lists')
        .select('*')
        .eq('user_id', user?.id)
        .order('position', { ascending: true });

      if (error) throw error;

      if (!data || data.length === 0) {
        // Create default list for new users
        const defaultListId = await addList("My First List");
        if (defaultListId) {
          const defaultList = {
            id: defaultListId,
            name: "My First List",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            user_id: user?.id || '',
            position: 0
          };
          setLists([defaultList]);
          setActiveListId(defaultListId);

          // Check if user had local tasks and migrate them to the first list
          await migrateLocalTasksToFirstList(defaultListId);
        }
      } else {
        setLists(data);
        
        // If there are lists, check if we need to set an active list
        if (data.length > 0) {
          const savedActiveListId = localStorage.getItem('activeListId');
          const validSavedList = savedActiveListId && data.find(list => list.id === savedActiveListId);
          
          if (validSavedList) {
            setActiveListId(savedActiveListId);
          } else {
            // Set the first list as active if no valid saved list
            setActiveListId(data[0].id);
          }

          // Check if user had local tasks and migrate them to the first list
          await migrateLocalTasksToFirstList(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      toast.error("There was a problem loading your lists.");
    } finally {
      setLoading(false);
    }
  };

  const migrateLocalTasksToFirstList = async (listId: string) => {
    const localTasks = localStorage.getItem(LOCAL_TASKS_KEY);
    if (localTasks && session) {
      try {
        const parsedTasks = JSON.parse(localTasks);
        if (Array.isArray(parsedTasks) && parsedTasks.length > 0) {
          // Migrate each local task to the first real list
          for (const task of parsedTasks) {
            const { error } = await supabase
              .from('tasks')
              .insert({
                title: task.title,
                detail: task.detail || '',
                status: task.status || 'to-do',
                list_id: listId,
                user_id: session.user.id,
                position: task.position || 0,
                total_time_spent: task.totalTimeSpent || 0,
                pomodoros_completed: task.pomodorosCompleted || 0
              });
            
            if (error) {
              console.error('Failed to migrate task:', error);
            }
          }
          
          // Clear local storage after successful migration
          localStorage.removeItem(LOCAL_TASKS_KEY);
          toast.success('Your local task has been migrated to your account!');
        }
      } catch (error) {
        console.error('Failed to migrate local tasks:', error);
      }
    }
  };

  const addList = async (name: string): Promise<string | null> => {
    if (!session) return null;

    try {
      const position = lists.length;
      const { data, error } = await supabase
        .from('lists')
        .insert({
          name,
          user_id: user?.id,
          position
        })
        .select()
        .single();

      if (error) throw error;

      setLists(prev => [...prev, data]);
      setActiveListId(data.id);
      toast.success(`List "${name}" created successfully!`);
      
      return data.id;
    } catch (error) {
      console.error('Error creating list:', error);
      toast.error("There was a problem creating your list.");
      return null;
    }
  };

  const updateList = async (id: string, name: string): Promise<void> => {
    if (!session) return;

    try {
      const { error } = await supabase
        .from('lists')
        .update({ name })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;

      setLists(prev => prev.map(list => 
        list.id === id ? { ...list, name } : list
      ));
      
      toast.success(`List renamed to "${name}"`);
    } catch (error) {
      console.error('Error updating list:', error);
      toast.error("There was a problem updating your list.");
    }
  };

  const deleteList = async (id: string): Promise<void> => {
    if (!session) return;

    if (lists.length <= 1) {
      toast.error("You must have at least one list.");
      return;
    }

    try {
      const { error } = await supabase
        .from('lists')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;

      const updatedLists = lists.filter(list => list.id !== id);
      setLists(updatedLists);
      
      if (activeListIdState === id && updatedLists.length > 0) {
        setActiveListId(updatedLists[0].id);
      }
      
      toast.success("List deleted successfully.");
    } catch (error) {
      console.error('Error deleting list:', error);
      toast.error("There was a problem deleting your list.");
    }
  };

  const reorderLists = async (sourceIndex: number, destinationIndex: number): Promise<void> => {
    if (!session) return;

    try {
      const reorderedLists = [...lists];
      const [removed] = reorderedLists.splice(sourceIndex, 1);
      reorderedLists.splice(destinationIndex, 0, removed);

      const updatedLists = reorderedLists.map((list, index) => ({
        ...list,
        position: index
      }));

      setLists(updatedLists);

      // Update positions in database
      const updates = updatedLists.map(list => 
        supabase
          .from('lists')
          .update({ position: list.position })
          .eq('id', list.id)
          .eq('user_id', user?.id)
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('Error reordering lists:', error);
      toast.error("There was a problem reordering your lists.");
    }
  };

  // Fetch lists when user changes or component mounts
  useEffect(() => {
    fetchLists();
  }, [session]);

  // Provide lists - for authenticated users, real lists; for non-authenticated, the default local list
  const effectiveLists = session ? lists : [defaultLocalList];
  const effectiveActiveListId = session ? activeListIdState : LOCAL_LIST_ID;

  return (
    <ListContext.Provider value={{
      lists: effectiveLists,
      activeListId: effectiveActiveListId,
      setActiveListId,
      addList,
      updateList,
      deleteList,
      reorderLists,
      loading,
      showCreateListModal,
      setShowCreateListModal
    }}>
      {children}
    </ListContext.Provider>
  );
};

export const useLists = () => {
  const context = useContext(ListContext);
  if (context === undefined) {
    throw new Error('useLists must be used within a ListProvider');
  }
  return context;
};